import { AttachmentReference, FileType, ResourceType } from "../models/attachmentReference";
import { StorageReport, StorageReportTaxDetail } from "../models/report";
import { CustomerReportConfig, CustomerReportSend, DailyReport } from "../models/customer-report";
import { ChangePasswordArgs } from "../models/dto/changePasswordArgs";
import { Destination, MexalLocation, NewDestination } from "../models/location";
import { LoginArgs, LoginResponse } from "../models/login";
import { ProductBrowse } from "../models/mexal/product-browse";
import { RegistryBrowse } from "../models/mexal/registry-browse";
import { PagedResult, PageRequest } from "../models/paging";
import { Setting } from "../models/setting";
import {
    AppliedTax, AppliedTaxOccurrence, CloseUnloadingArgs, CloseUnloadingResult, Contract, ContractOwner, ContractStock,
    CustomsClearance, DeliverLoadingOrderArgs, LoadingOrder, LoadingOrderPrint, LoadingOrderSplitResult, LoadingOrderStatus, Mad, MadReopenArgs, PriceList, Ship, ShipUnloadingArgs, ShipType,
    SiloMovement, SiloStockLine, SiloTransferArgs, Tax, TaxScopeType
} from "../models/storage";
import { User } from "../models/user";
import apiClient from "./api-client";

export class ApiService {

    //#region Authentication
    // Le schermate di autenticazione mostrano l'errore inline: niente notifica automatica,
    // altrimenti l'utente lo vedrebbe due volte.
    public Login(args: LoginArgs) {
        return apiClient.post<LoginResponse>(`/auth/login`, args, { skipErrorNotification: true });
    }

    public GetAllUsers() {
        return apiClient.get<User[]>(`/auth/user`);
    }

    public GetUserProfile(id: string) {
        return apiClient.get<User>(`/auth/user/${id}`);
    }

    /** Crea l'utente; mailError dice perché la mail per impostare la password non è partita. */
    public RegisterUser(user: User) {
        return apiClient.put<{ succeeded: boolean, mailError?: string }>(`/auth/user`, user);
    }

    public UpdateUser(user: User) {
        return apiClient.post<string>(`/auth/user`, user);
    }

    public ChangeUserPassword(args: ChangePasswordArgs) {
        return apiClient.post<boolean>(`/auth/user/change-password`, args);
    }

    // POST e non GET: password e token non devono viaggiare in querystring,
    // dove finirebbero in access log, cronologia del browser e header Referer.
    public ChangeUserPasswordLink(value: string) {
        return apiClient.post<void>(`/auth/user/reset-password-link`, { value }, { skipErrorNotification: true });
    }

    public ResetPassword(userId: string, password: string, token: string) {
        return apiClient.post<void>(`/auth/user/reset-password`, { userId, password, token }, { skipErrorNotification: true });
    }

    public DeleteUser(id: string) {
        return apiClient.delete<User>(`/auth/user/${id}`);
    }

    public Logout() {
        return apiClient.get(`/auth/logout`);
    }
    //#endregion

    //#region Data
    // Testo passato con params e non concatenato nell'URL: axios lo codifica, e una ricerca
    // come "Rossi & Figli" non spezza più la querystring.
    public SearchProducts(text: string) {
        return apiClient.get<ProductBrowse[]>(`/data/product`, { params: { text }, skipLoader: true });
    }

    public SearchRegistries(text: string, registryType: 'C' | 'F') {
        return apiClient.get<RegistryBrowse[]>(`/data/registry`, { params: { text, registryType }, skipLoader: true });
    }

    // Senza notifica: se Mexal non risponde, il lookup dei silos ripiega sul codice digitato
    // a mano, e un errore rosso su ogni pagina che lo usa sarebbe solo rumore.
    public GetLocations() {
        return apiClient.get<MexalLocation[]>(`/data/location`, { skipErrorNotification: true });
    }

    /** Indirizzi di spedizione di un cliente, per la destinazione di M.A.D. e buoni. */
    public GetDestinations(accountCode: string) {
        return apiClient.get<Destination[]>(`/data/destination`, { params: { accountCode }, skipErrorNotification: true, skipLoader: true });
    }

    /** Crea su Mexal un indirizzo di spedizione del cliente; restituisce il suo codice. */
    public CreateDestination(destination: NewDestination) {
        return apiClient.post<string>(`/data/destination`, destination);
    }
    //#endregion

    //#region Anagrafiche
    public GetShipTypes() {
        return apiClient.get<ShipType[]>(`/ship/type`);
    }

    public SaveShipType(shipType: ShipType) {
        return apiClient.put<string>(`/ship/type`, shipType);
    }

    public DeleteShipType(id: string) {
        return apiClient.delete<void>(`/ship/type/${id}`);
    }

    public GetShips(includeInactive = false) {
        return apiClient.get<Ship[]>(`/ship`, { params: { includeInactive } });
    }

    public SaveShip(ship: Ship) {
        return apiClient.put<string>(`/ship`, ship);
    }

    public DeleteShip(id: string) {
        return apiClient.delete<void>(`/ship/${id}`);
    }

    public GetPriceLists(includeInactive = false) {
        return apiClient.get<PriceList[]>(`/price-list`, { params: { includeInactive } });
    }

    public GetPriceList(id: string) {
        return apiClient.get<PriceList>(`/price-list/${id}`);
    }

    public SavePriceList(priceList: PriceList) {
        return apiClient.put<string>(`/price-list`, priceList);
    }

    public DeletePriceList(id: string) {
        return apiClient.delete<void>(`/price-list/${id}`);
    }

    public GetTaxes(includeInactive = false) {
        return apiClient.get<Tax[]>(`/tax`, { params: { includeInactive } });
    }

    public SaveTax(tax: Tax) {
        return apiClient.put<string>(`/tax`, tax);
    }

    public DeleteTax(id: string) {
        return apiClient.delete<void>(`/tax/${id}`);
    }

    public GetAppliedTaxes(scopeType: TaxScopeType, scopeId: string) {
        return apiClient.get<AppliedTax[]>(`/tax/applied`, { params: { scopeType, scopeId } });
    }

    public ApplyTax(appliedTax: AppliedTax) {
        return apiClient.put<string>(`/tax/applied`, appliedTax);
    }

    public RemoveAppliedTax(id: string) {
        return apiClient.delete<void>(`/tax/applied/${id}`);
    }

    public AddTaxOccurrence(occurrence: Partial<AppliedTaxOccurrence>) {
        return apiClient.post<string>(`/tax/applied/occurrence`, occurrence);
    }

    /** Solo se non ancora fatturata. */
    public DeleteTaxOccurrence(id: string) {
        return apiClient.delete<void>(`/tax/applied/occurrence/${id}`);
    }

    /** Maturazioni automatiche sulla giacenza (l'assicurazione) di un contratto nel periodo, come le calcola il report. */
    public GetContractRecurringTaxes(contractId: string, from: string, to: string) {
        return apiClient.get<StorageReportTaxDetail[]>(`/report/contract/${contractId}/recurring-taxes`, { params: { from, to } });
    }
    //#endregion

    //#region Contratti
    /**
     * Senza `page` restituisce tutti i contratti: è quello che serve alle tendine, dove una
     * pagina parziale nasconderebbe delle scelte. Gli elenchi passano sempre la pagina.
     */
    public GetContracts(customerCode?: string, includeClosed = false, page?: PageRequest) {
        return apiClient.get<PagedResult<Contract>>(`/contract`, { params: { customerCode, includeClosed, ...page } });
    }

    /** Clienti con contratti aperti: gli unici che possono cedere merce con una M.A.D. */
    public GetContractOwners() {
        return apiClient.get<ContractOwner[]>(`/contract/owners`);
    }

    public GetContract(id: string) {
        return apiClient.get<Contract>(`/contract/${id}`);
    }

    public SaveContract(contract: Contract) {
        return apiClient.put<string>(`/contract`, contract);
    }

    public DeleteContract(id: string) {
        return apiClient.delete<void>(`/contract/${id}`);
    }

    public CloseUnloading(args: CloseUnloadingArgs) {
        return apiClient.post<CloseUnloadingResult>(`/contract/close-unloading`, args);
    }

    /** Quello che la conferma della fine scarico farebbe, senza salvare nulla. */
    public PreviewCloseUnloading(args: CloseUnloadingArgs) {
        // L'errore (es. listino mancante) lo mostra la modale stessa, non una notifica.
        return apiClient.post<CloseUnloadingResult>(`/contract/close-unloading/preview`, args, { skipErrorNotification: true, skipLoader: true });
    }

    public SaveCustomsClearance(clearance: Partial<CustomsClearance>) {
        return apiClient.put<string>(`/contract/customs-clearance`, clearance);
    }

    public DeleteCustomsClearance(id: string) {
        return apiClient.delete<void>(`/contract/customs-clearance/${id}`);
    }
    //#endregion

    //#region Movimenti
    public GetMovements(contractId: string) {
        return apiClient.get<SiloMovement[]>(`/movement/contract/${contractId}`);
    }

    public GetStockByLocation(contractId: string) {
        return apiClient.get<SiloStockLine[]>(`/movement/contract/${contractId}/stock`);
    }

    /** Giacenza Mexal del contratto e a chi è promessa: M.A.D., buoni aperti, libero del proprietario. */
    public GetContractStock(contractId: string) {
        return apiClient.get<ContractStock>(`/movement/contract/${contractId}/stock-breakdown`);
    }

    public RegisterShipUnloading(args: ShipUnloadingArgs) {
        return apiClient.post<string>(`/movement/ship-unloading`, args);
    }

    public RegisterTransfer(args: SiloTransferArgs) {
        return apiClient.post<string>(`/movement/transfer`, args);
    }

    public ReverseMovement(id: string, reason?: string) {
        return apiClient.post<string>(`/movement/${id}/reverse`, null, { params: { reason } });
    }
    //#endregion

    //#region M.A.D. e buoni di carico
    public GetMads(transferorCode?: string, onlyValid = false, page?: PageRequest, contractId?: string) {
        return apiClient.get<PagedResult<Mad>>(`/mad`, { params: { transferorCode, onlyValid, contractId, ...page } });
    }

    public GetMad(id: string) {
        return apiClient.get<Mad>(`/mad/${id}`);
    }

    public SaveMad(mad: Mad) {
        return apiClient.put<string>(`/mad`, mad);
    }

    public CloseMad(id: string) {
        return apiClient.post<void>(`/mad/${id}/close`);
    }

    public ReopenMad(id: string, args: MadReopenArgs) {
        return apiClient.post<void>(`/mad/${id}/reopen`, args);
    }

    public DeleteMad(id: string) {
        return apiClient.delete<void>(`/mad/${id}`);
    }

    public GetLoadingOrdersByMad(madId: string) {
        return apiClient.get<LoadingOrder[]>(`/mad/${madId}/loading-order`);
    }

    public GetLoadingOrders(filter: { madId?: string, from?: string, to?: string, status?: LoadingOrderStatus }, page?: PageRequest) {
        return apiClient.get<PagedResult<LoadingOrder>>(`/loading-order`, { params: { ...filter, ...page } });
    }

    public GetLoadingOrder(id: string) {
        return apiClient.get<LoadingOrder>(`/loading-order/${id}`);
    }

    public CreateLoadingOrder(order: Partial<LoadingOrder>) {
        return apiClient.post<string>(`/loading-order`, order);
    }

    //#region Riepiloghi ai clienti
    public GetCustomerReports() {
        return apiClient.get<CustomerReportConfig[]>(`/customer-report`);
    }

    public SaveCustomerReport(config: CustomerReportConfig) {
        return apiClient.put<string>(`/customer-report`, config);
    }

    public DeleteCustomerReport(id: string) {
        return apiClient.delete<void>(`/customer-report/${id}`);
    }

    /** Invii della configurazione; con `date` solo quelli di quel giorno. */
    public GetCustomerReportSends(id: string, date?: string) {
        return apiClient.get<CustomerReportSend[]>(`/customer-report/${id}/sends`, { params: { date } });
    }

    public GetDailyReport(customerCode: string, date: string) {
        return apiClient.get<DailyReport>(`/customer-report/daily`, { params: { customerCode, date } });
    }

    /** Tracciato nel formato del cliente: errore se per lui non ne esiste uno. */
    public GetCustomExport(customerCode: string, date: string) {
        return apiClient.get<Blob>(`/customer-report/custom-export`, { params: { customerCode, date }, responseType: 'blob' });
    }

    /** Invio: i PDF composti nel browser viaggiano come file, bolle e tracciato li aggiunge il server. */
    public SendCustomerReport(id: string, date: string, files: { name: string, content: Blob }[]) {
        const form = new FormData();
        form.append('date', date);
        files.forEach(file => form.append('files', file.content, file.name));
        // Senza, varrebbe il JSON predefinito del client e il server risponderebbe 415.
        return apiClient.post<CustomerReportSend>(`/customer-report/${id}/send`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
    //#endregion

    /** PDF della bolla di scarico, stampato da Mexal. */
    public GetDeliveryNotePdf(id: string) {
        return apiClient.get<Blob>(`/loading-order/${id}/delivery-note`, { responseType: 'blob' });
    }

    public GetLoadingOrderPrint(id: string) {
        return apiClient.get<LoadingOrderPrint>(`/loading-order/${id}/print`);
    }

    /** Ritiro oltre il residuo: buono sul residuo più M.A.D. provvisoria con il suo buono per l'eccedenza. */
    public CreateLoadingOrderSplit(order: Partial<LoadingOrder>) {
        return apiClient.post<LoadingOrderSplitResult>(`/loading-order/split`, order);
    }

    /** Bollettazione: registra i prelievi pesati e crea la bolla su Mexal. */
    public DeliverLoadingOrder(args: DeliverLoadingOrderArgs) {
        return apiClient.post<void>(`/loading-order/deliver`, args);
    }

    public CancelLoadingOrder(id: string, reason?: string) {
        return apiClient.post<void>(`/loading-order/${id}/cancel`, null, { params: { reason } });
    }
    //#endregion

    //#region Report
    /** Magazzinaggio e oneri maturati nel periodo. Di sola lettura: si può ricaricare a piacere. */
    public GetStorageReport(from: string, to: string, customerCode?: string) {
        return apiClient.get<StorageReport>(`/report/storage`, { params: { from, to, customerCode } });
    }
    //#endregion

    //#region Attachment
    public GetAttachmentsByReferenceId(id: string, fileType = FileType.Attachment) {
        return apiClient.get<AttachmentReference[]>(`/attachment/${id}?fileType=${fileType}`);
    }

    public GetAttachmentsByFileType(resourceType: ResourceType, fileType: FileType) {
        return apiClient.get<AttachmentReference[]>(`/attachment?resourceType=${resourceType}&fileType=${fileType}`);
    }

    /**
     * Attende davvero il completamento degli upload. Prima il forEach lanciava le
     * promise senza attenderle: il chiamante proseguiva subito e un upload fallito
     * passava del tutto inosservato.
     */
    public UploadFile(id: string, files: File[], resourceType: ResourceType) {
        return Promise.all(files.map(file => {
            const formData = new FormData();
            formData.append("file", file, file.name);
            return this.UploadAttachment(id, formData, resourceType);
        }));
    }

    public UploadAttachment(id: string, formData: FormData, resourceType: ResourceType, fileType = FileType.Attachment) {
        return apiClient.post(`/attachment/${id}/${fileType}/${resourceType}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
    }

    public DeleteAttachment(id: string) {
        return apiClient.delete(`/attachment/${id}`);
    }

    public DeleteAttachmentsByReferenceId(referenceId: string) {
        return apiClient.delete(`/attachment/reference/${referenceId}`);
    }
    //#endregion

    //#region Setting
    public GetAllSettings() {
        return apiClient.get<Setting[]>(`/setting`);
    }

    public GetSettingByName(name: string) {
        return apiClient.get<Setting>(`/setting/${name}`);
    }

    public SaveSetting(setting: Setting) {
        return apiClient.put<void>(`/setting/`, setting);
    }

    public TestSendMail() {
        return apiClient.get<boolean>(`/mail/test`);
    }
    //#endregion

}

/**
 * Istanza condivisa: la classe non ha stato, e crearne una nuova a ogni render
 * di ogni componente non serviva a niente.
 */
export const apiService = new ApiService();
